import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FilterMatchMode, LazyLoadEvent, MessageService, SelectItem } from 'primeng/api';
import { helperService } from 'src/app/utils/helper';
import { OrganizationService } from './organization.service';
import { matchModeOptionsDateList, organization } from 'src/app/utils/filterDropDownData';
import { SavedTableState, TableStateService } from 'src/app/shared/table-state.service';
import { AuthGuard } from 'src/app/account/auth/app.gaurd';

@Component({
    selector: 'app-organization',
    templateUrl: './organization.component.html',
    styleUrls: ['./organization.component.scss'],
    standalone: false
})
export class OrganizationComponent implements OnInit {

  @ViewChild('table') orgGrid: any;
  orgData: any;
  selectedRow: any;
  cols: any[] = [];
  matchModeOptionsString?: SelectItem[];
  matchModeOptionsDate?: SelectItem[];
  matchModeOptionsNumber?: SelectItem[];
  loading: boolean = true;
  rows = 10
  viewOnly: Boolean = false;
  viewOrg: Boolean = false;
  orgDialog: Boolean = false;
  private lastTableLazyLoadEvent: any = null;
  totalRecords: number = 0;
  first: number = 1;
  title: any;
  projectMatterFilter: any;
  projectOrgFilter: any;
  ViewOrgName: any;
  matterOrgFilter: any;
  statuses: any;
  orgSubscription: any;
  initialLoadDone: boolean = false;
  public defaultFilters: { [s: string]: any } = {};


  constructor(private filterService: helperService, private orgService: OrganizationService, private tableStateService: TableStateService,
    public auth: AuthGuard) { }

  ngOnInit(): void {

    this.cols = [
      { field: 'name', header: 'Organisation Name' },
      { field: 'organizationType', header: 'Organisation Type' },
      { field: 'quickbookId', header: '' },
      { field: 'accountOwner', header: 'Account Owner' },
      { field: 'createdOn', header: 'Date Created', data: true, format: `MM/dd/yyyy` },
      { field: 'status', header: 'Status' },
      { field: 'activePrjCount', header: 'Active Projects' },
    ];

    this.matchModeOptionsString = [
      { label: 'Equals', value: FilterMatchMode.EQUALS },
      { label: 'Contains', value: FilterMatchMode.CONTAINS },
    ];

    this.matchModeOptionsDate = matchModeOptionsDateList;

    this.matchModeOptionsNumber = [
      { label: 'Equal', value: FilterMatchMode.EQUALS },
      { label: 'Greater than', value: FilterMatchMode.GREATER_THAN },
      { label: 'Less than', value: FilterMatchMode.LESS_THAN },
      { label: 'Between', value: FilterMatchMode.BETWEEN },
    ];

    this.statuses = organization.statuses;

    const savedState = this.tableStateService.getState('org_grid', this.auth.jwtData.email);

    if (savedState && !this.initialLoadDone) {
      this.restoreState(savedState);
    }

    const target = history.state?.openOrganization;
    if (target?.id) {
      history.replaceState({ ...history.state, openOrganization: null }, '');
      this.orgService.getOrganizationById(target.id).subscribe((res: any) => {
        if (res?.data) this.clicked(this.normalizeOrgRow(res.data));
      });
    }
  }

  loadOrgData(event: LazyLoadEvent) {
    this.saveState('org_grid', this.auth.jwtData.email, event, {});
    this.loading = true;
    this.lastTableLazyLoadEvent = event;
    event.first = event.first ? event.first : 1;
    event.rows = event.rows ? event.rows : 10;
    let payload = this.filterService.generateEventPayload(event);
    if (this.orgSubscription)
      this.orgSubscription.unsubscribe();
    this.orgSubscription = this.orgService.getAllOrganizations(Math.floor(event.first / event.rows + 1), event.rows, payload).subscribe(res => {
      this.totalRecords = res.customValues.totalNumberOfRecords;
      this.loading = false;
      res.data?.forEach((element: any) => this.normalizeOrgRow(element));
      this.orgData = res.data;
    })
  }

  private normalizeOrgRow(element: any): any {
    element.isForceRequestedBy = element.isForceRequestedBy == 'Yes' ? true : false;
    element.requestedPersons = new Set((element.requestedPersons || []).map((val: any) => val.name));
    return element;
  }

  closeDialog(evnt?: any) {  // v13-migration: param optional (template calls closeDialog() with 0 args; was undefined on v12)
    this.orgDialog = false;
    this.viewOrg = false;
    this.viewOnly = false;
    this.loadOrgData(this.lastTableLazyLoadEvent);
  }

  clicked(data: any) {
    this.selectedRow = data;
    this.matterOrgFilter = this.selectedRow.id;
    this.projectOrgFilter = this.selectedRow.id;
    this.viewOrg = true;
    this.ViewOrgName = this.selectedRow.name;
  }

  editOrg(readable: boolean, orgData: any) {
    this.orgDialog = true;
    orgData.prefDisbursementType = orgData.prefDisbursementType || "Credit Card on File";
    this.selectedRow = orgData;
    if (readable) {
      this.title = "View Organization";
      this.viewOnly = true;
    }
    else {
      this.title = "Edit Organization";
      this.viewOnly = false;
    }
  }

  backToOrgGrid() {
    this.viewOrg = false;
    this.loadOrgData(this.lastTableLazyLoadEvent);
  }

  restoreState(saved: any) {
    const event = saved.event;

    setTimeout(() => {
      if (this.orgGrid) {

        const filtersAreValid = event.filters && Object.keys(event.filters).length > 0;

        // 2. Assign filters: Use saved filters if valid, otherwise use the captured default filters.
        if (filtersAreValid) {
          this.orgGrid.filters = event.filters;
        } else if (Object.keys(this.defaultFilters).length > 0) {
          // 🔥 Use the default structure as a fallback to prevent empty filter popups
          this.orgGrid.filters = this.defaultFilters;
        }

        // Restore sorting
        this.orgGrid.sortField = event.sortField ?? '';
        this.orgGrid.sortOrder = event.sortOrder ?? 0;

        // 🔥 force PrimeNG to show sorting icons
        this.orgGrid.sortSingle();

        // Restore paginator
        this.orgGrid.first = event.first ?? 0;
        this.orgGrid.rows = event.rows ?? 10;

        // Trigger reload
        this.orgGrid.onLazyLoad.emit(event);
      }
    }, 50);

    this.initialLoadDone = true;
  }

  saveState(screen: string, userId: string, event: LazyLoadEvent, customFilters: any) {
    const state: SavedTableState = {
      event: {
        first: event.first,
        rows: event.rows,
        sortField: event.sortField,
        sortOrder: event.sortOrder,
        filters: event.filters
      },
      customFilters: { ...customFilters }
    };

    this.tableStateService.saveState(screen, userId, state);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.orgGrid && this.orgGrid.filters) {
        this.defaultFilters = JSON.parse(JSON.stringify(this.orgGrid.filters));
      }
    });
  }


}
